import {Box, LinearProgress, Tab, Tabs, useTheme } from '@mui/material'
import { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import PageHeader from '../../../components/PageHeader'
import { useAppDispatch, useAppSelector } from '../../../redux/store'
import {
  getPracticeTasks,
  selectCustomer,
  selectLoadingState,
  selectTaskLogs,
  selectTasks,
  clearPracticeTasks,
} from './slice'
import TaskList from './list/TasksList'
import TaskHistory from './history/TaskHistory'
import { OpenTaskWarningModal, Task, TaskLogPayload } from './types'
import { selectCurrentUser } from '../../../redux/user'
import TaskModal from './list/TaskModal'
import { useSearchParams } from 'react-router-dom'
import { faTasks } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { getIsOneTimeTask, getNextDueDate, getTaskLogPayload } from './utils/helpers'
import TaskWarningModal from './list/TaskWarningModal'
import TaskButtonGroup from './TaskButtonGroup'
import { addError, addSuccess } from '../../../redux/alerts'
import useCustomerNumber from '../../../hooks/useCustomerNumber'
import * as service from './service'
import { TaskLogStatus } from './enums'
import useTeamMembers from '../../../hooks/useTeamMembers'

const Tasks = () => {
  const pageTabs = ['Tasks', 'History']
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const customerNumber = useCustomerNumber()
  const users = useTeamMembers()
  const customer = useAppSelector(selectCustomer)

  const loadingState = useAppSelector(selectLoadingState)
  const tasks = useAppSelector(selectTasks)
  const taskLogs = useAppSelector(selectTaskLogs)
  const currentUser = useAppSelector(selectCurrentUser)

  const [tabIndex, setTabIndex] = useState(0)
  const [search, setSearch] = useState('')
  
  const [selectedTasks, setSelectedTasks] = useState<Map<number, Task>>(new Map())
  const [isTaskModalOpen, setTaskModalOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [openTaskWarningModal, setOpenTaskWarningModal] = useState<OpenTaskWarningModal>({ open: false, tasks: null })
  
  const activeTasks = useMemo(() => {
    return tasks && tasks.filter(task => task.active)
  }, [tasks])

  const missedTaskCount = useMemo(() => {
    const thirtyDaysAgo = dayjs().subtract(30, 'days')

    const missed = taskLogs && taskLogs.filter(log => {
      const isMissed = log.status === 'missed'
      const isAfterThirtyDaysAgo = dayjs(log.date).isAfter(thirtyDaysAgo)

      return isMissed && isAfterThirtyDaysAgo
    })

    return missed?.length ?? 0
  }, [taskLogs])

  const filteredTasks = useMemo(() => {
    const tasks = activeTasks ?? []
    const cleanSearch = search.trim().toLowerCase()

    const filtered = cleanSearch
        ? tasks.filter(task => task.name && task.name.trim().toLowerCase().includes(cleanSearch))
        : tasks

    return filtered
  }, [search, activeTasks])

  useEffect(() => {
    const needsData = Boolean(customerNumber) && !customer && loadingState === 'not-asked'
    const clearData = customer && customerNumber !== customer.customer_number

    if (needsData) {
      dispatch(getPracticeTasks(customerNumber))
    } else if (clearData) {
      dispatch(clearPracticeTasks())
    }
  }, [customerNumber, customer, loadingState])

  useEffect(() => {
    const completeTaskFromSearchParams = async () => {
      if (currentUser && activeTasks && searchParams) {
        const selectedTaskId = searchParams.get('taskId')
        if (selectedTaskId) {
          const task = activeTasks.find(t => t.id === parseInt(selectedTaskId))
          if (task) {
            const isOneTime = getIsOneTimeTask(task)
            const payload: TaskLogPayload = getTaskLogPayload(task, currentUser, TaskLogStatus.Complete)
            
            await service.createTaskLog(customerNumber, payload)
            
            if (isOneTime) {
              await service.deleteTask(customerNumber, task)
            }

            await dispatch(getPracticeTasks(customerNumber))

            setSearchParams({})
            dispatch(addSuccess('Your task has successfully been completed.'))
            setTabIndex(1)
          } else {
            setTabIndex(0)
            dispatch(addError('This instance of this task has already been completed and cannot be completed again'))
          }
        }
      }
    }
    completeTaskFromSearchParams()
  }, [currentUser, activeTasks, searchParams])

  const handleCompleteTasks = async (tasks: Task[]) => {
    const hasFutureDates = tasks.some(task => {
      const nextDueDate = dayjs(getNextDueDate(task))
      const isFutureDate = nextDueDate.isAfter(dayjs(), 'day')

      return isFutureDate
    })

    if (hasFutureDates) {
      setOpenTaskWarningModal({ open: true, tasks })
    } else if (currentUser) {
      const oneTimeTasks = tasks.filter(task => getIsOneTimeTask(task))

      await Promise.all(
        tasks.map(task => {
          const payload: TaskLogPayload = getTaskLogPayload(task, currentUser, TaskLogStatus.Complete)
          return service.createTaskLog(customerNumber, payload)
        })
      )

      await Promise.all(
        oneTimeTasks.map(task => service.deleteTask(customerNumber, task))
      )

      await dispatch(getPracticeTasks(customerNumber))
      setSelectedTasks(new Map())
      dispatch(addSuccess('Your task has successfully been completed.'))
    }
  }

  const handleEditTask = (task: Task) => {
    setSelectedTask(task)
    setTaskModalOpen(true)
  }

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
  }

  const TabContent = () => {
    const tabContents = [
      <TaskList
        users={users}
        tasks={filteredTasks}
        selectedTasks={selectedTasks}
        setSelectedTasks={setSelectedTasks}
        handleEditTask={handleEditTask}
        handleCompleteTasks={handleCompleteTasks}
      />,
      <TaskHistory
        users={users}
        tasks={tasks}
        taskLogs={taskLogs}
      />
    ]
    return tabContents[tabIndex]
  }

  return (
    <>
      <PageHeader iconName={faTasks} headerName={'Tasks'} size={'26px'} />
      {loadingState === 'loading' && (<LinearProgress />)}
      <Box sx={{ color: theme.palette.color50555b.main }} className="container">
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-end m-b-16">
          <Tabs value={tabIndex} onChange={handleTabChange}>
            <Tab label={pageTabs[0]} />
            <Tab
              label={
                <Box className="flex flex-align-center" columnGap="6px">
                  {pageTabs[1]}
                  {
                    missedTaskCount > 0 && (
                      <Box className="flex flex-align-center background-danger task-history-counter">
                        { missedTaskCount }
                      </Box>
                    )
                  }
                </Box>
              }
            />
          </Tabs>
        </Box>
        <TaskButtonGroup
          tabIndex={tabIndex}
          selectedTasks={selectedTasks}
          setSearch={setSearch}
          setTaskModalOpen={setTaskModalOpen}
          handleCompleteTasks={handleCompleteTasks}
        />
        <TabContent />
      </Box>
      {
        isTaskModalOpen && currentUser && (
          <TaskModal
            open={isTaskModalOpen}
            users={users}
            task={selectedTask}
            currentUser={currentUser}
            customerNumber={customerNumber}
            setTaskModalOpen={setTaskModalOpen}
            setSelectedTask={setSelectedTask}
          />
        )
      }
      {
        openTaskWarningModal.open && openTaskWarningModal.tasks && (
          <TaskWarningModal
            open={openTaskWarningModal.open}
            tasks={openTaskWarningModal.tasks}
            currentUser={currentUser}
            setOpenTaskWarningModal={setOpenTaskWarningModal}
            setSelectedTasks={setSelectedTasks}
          />
        )
      }      
    </>
  )
}

export default Tasks